import React from 'react';
import { Box, Text, VStack, HStack, Icon, Tooltip, IconButton, Skeleton } from '@chakra-ui/react';
import { mdiInformationOutline } from '@mdi/js';
import { UsageProgress } from './UsageProgress';

interface ProgressData {
  label: string;
  value: number;
  total: number;
  variant?: 'primary' | 'secondary';
}

interface SummaryData {
  label: string;
  value: string;
}

interface UsageCardProps {
  icon: React.ReactElement;
  title: string;
  value?: string;
  progressData?: ProgressData[];
  summaryData?: SummaryData[];
  loading?: boolean;
}

export const UsageCard: React.FC<UsageCardProps> = ({
  icon,
  title,
  value,
  progressData,
  summaryData,
  loading = false
}) => {
  return (
    <Box
      bg="white"
      borderRadius="lg"
      p={5}
      border="1px"
      borderColor="gray.200"
      minH="168px"
      position="relative"
    >
      <HStack spacing={2} align="center" mb={4}>
        <Box flexShrink={0}>
          {icon}
        </Box>
        <Text color="gray.600" fontWeight="medium">
          {title}
        </Text>
        <Tooltip label="More information coming soon">
          <IconButton
            size="xs"
            variant="ghost"
            aria-label="Information"
            icon={
              <Icon boxSize="16px">
                <path d={mdiInformationOutline} />
              </Icon>
            }
          />
        </Tooltip>
      </HStack>

      {loading ? (
        <>
          {value !== undefined && (
            <Box position="absolute" bottom="6" left="6" right="6" w="calc(100% - 48px)" overflow="hidden">
              <Skeleton height="30px" width="104px" />
            </Box>
          )}

          {progressData && (
            <VStack align="stretch" spacing={3}>
              {progressData.map((_, index) => (
                <VStack key={index} align="stretch" spacing={1}>
                  <HStack justify="space-between">
                    <Skeleton height="16px" width="80px" />
                    <Skeleton height="16px" width="60px" />
                  </HStack>
                  <Skeleton height="8px" width="100%" />
                </VStack>
              ))}
            </VStack>
          )}

          {summaryData && (
            <HStack position="absolute" bottom="6" left="6" right="6" spacing={4} align="end" w="calc(100% - 48px)">
              {summaryData.map((item, index) => (
                <VStack key={index} align="start" spacing={1} flex={1} minW={0} overflow="hidden">
                  <Text color="gray.800">
                    {item.label}
                  </Text>
                  <Skeleton height="30px" width="104px" borderRadius="md" />
                </VStack>
              ))}
            </HStack>
          )}
        </>
      ) : (
        <>
          {value && (
            <Box position="absolute" bottom="6" left="6" right="6" w="calc(100% - 48px)" overflow="hidden">
              <Text 
                fontSize="4xl"
                fontWeight="bold" 
                color="gray.800" 
                lineHeight="1"
                isTruncated
                maxW="100%"
              >
                {value}
              </Text>
            </Box>
          )}

          {progressData && (
            <VStack align="stretch" spacing={3}>
              {progressData.map((progress, index) => (
                <UsageProgress
                  key={index}
                  label={progress.label}
                  value={progress.value}
                  total={progress.total}
                  variant={progress.variant}
                />
              ))}
            </VStack>
          )}

          {summaryData && (
            <HStack position="absolute" bottom="6" left="6" right="6" spacing={4} align="end" w="calc(100% - 48px)">
              {summaryData.map((item, index) => (
                <VStack key={index} align="start" spacing={1} flex={1} minW={0} overflow="hidden">
                  <Text color="gray.800">
                    {item.label}
                  </Text>
                  <Text 
                    color="gray.800" 
                    fontWeight="bold" 
                    fontSize="4xl"
                    lineHeight="1"
                    isTruncated
                    maxW="100%"
                  >
                    {item.value}
                  </Text>
                </VStack>
              ))}
            </HStack>
          )}
        </>
      )}
    </Box>
  );
};
